import { AccessTime, CalendarToday } from '@mui/icons-material';
import { Box, Skeleton, TableCell, TableRow, Typography } from '@mui/material';
import EmptyState from 'components/EmptyState';
import TimelineIcon from 'components/TimelineIcon';
import dayjs from 'dayjs';
import { useTimelineDescription } from 'hooks';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Account } from 'types/Account';
import { SummaryTimeline, Timeline, TimelineTypeEnum } from 'types/timeline.types';
import useTimelineData from './useTimeline';

function EventsRows({ accountSelected }: { accountSelected: Account | null }) {
  const { data, loading } = useTimelineData({
    accountSelected,
  });

  const haveData = data && data.length > 0;
  const isEmpty = !loading && !haveData;

  return (
    <>
      {loading && <EventsRowsLoading accountSelected={accountSelected} />}
      {haveData &&
        data.map((event) => (
          <TableRow
            key={event.id}
            component={Link}
            to={`/events/${event.type}/${event.id}${
              accountSelected ? '' : `/${(event as SummaryTimeline).accountId}`
            }${
              event.type === TimelineTypeEnum.LOG && event?.event?.occurrenceId
                ? `?occurrenceId=${event.event.occurrenceId}`
                : ''
            }`}
            sx={{
              textDecoration: 'none',
            }}
            hover
          >
            <TableCell sx={{ py: 1, overflow: 'hidden' }} colSpan={3}>
              <EventDescriptionColumn {...{ event }} />
            </TableCell>
            {!accountSelected && (
              <TableCell sx={{ py: 1, overflow: 'hidden' }} colSpan={2}>
                {(event as SummaryTimeline).accountName} ({(event as SummaryTimeline).accountCode})
              </TableCell>
            )}
            <TableCell sx={{ py: 1, overflow: 'hidden' }}>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarToday sx={{ width: '0.8rem', mr: 0.5 }} />
                {dayjs(event.date).format('L')}
              </Box>
            </TableCell>
            <TableCell sx={{ py: 1, overflow: 'hidden' }}>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTime sx={{ width: '0.8rem', mr: 0.5 }} />
                {dayjs(event.date).format('LTS')}
              </Box>
            </TableCell>
          </TableRow>
        ))}
      {isEmpty && <EventsRowsEmpty />}
    </>
  );
}

function EventDescriptionColumn({ event }: { event: Timeline }) {
  const { title, description } = useTimelineDescription({ event });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
      <TimelineIcon type={event.type} IconProps={{ sx: { height: '0.8rem', width: '0.8rem' } }} />
      <Box component="span" sx={{ overflow: 'hidden' }}>
        {title}
        {description && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function EventsRowsLoading({ accountSelected }: { accountSelected: Account | null }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <TableRow key={index}>
          <TableCell colSpan={3}>
            <Skeleton />
          </TableCell>
          {!accountSelected && (
            <TableCell colSpan={2}>
              <Skeleton />
            </TableCell>
          )}
          <TableCell>
            <Skeleton />
          </TableCell>
          <TableCell>
            <Skeleton />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function EventsRowsEmpty() {
  const { t } = useTranslation(['_common', 'events']);
  return (
    <EmptyState
      sx={{
        position: 'absolute',
        left: '0',
        right: '0',
        bottom: '0',
        top: '0',
      }}
      title={t('_common:there_is_still_nothing_here')}
      description={t('events:there_is_no_event_for_this_account_in_this_period')}
    />
  );
}

export default EventsRows;
